import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { Theme } from "./theme";
import { darkTheme, lightTheme } from "./theme";
import { applySemanticColorsToDocument } from "./semanticColorsDom";
import { readInitialIsDark, saveDarkThemePreferenceToLocalStorage } from "./themePreferences";

export type SiteThemeContextValue = {
  isDark: boolean;
  setIsDark: (value: boolean) => void;
  toggleTheme: () => void;
};

const SiteThemeContext = createContext<SiteThemeContextValue | null>(null);

export function SiteThemeProvider({ children }: { children: React.ReactNode }) {
  const [isDark, setIsDarkState] = useState(false);

  useEffect(() => {
    setIsDarkState(readInitialIsDark());
  }, []);

  useEffect(() => {
    applySemanticColorsToDocument(isDark);
  }, [isDark]);

  const setIsDark = useCallback((value: boolean) => {
    setIsDarkState(value);
    saveDarkThemePreferenceToLocalStorage(value);
  }, []);

  const toggleTheme = useCallback(() => {
    setIsDarkState((prev) => {
      const next = !prev;
      saveDarkThemePreferenceToLocalStorage(next);
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ isDark, setIsDark, toggleTheme }),
    [isDark, setIsDark, toggleTheme]
  );

  return <SiteThemeContext.Provider value={value}>{children}</SiteThemeContext.Provider>;
}

export function useSiteTheme(): SiteThemeContextValue {
  const ctx = useContext(SiteThemeContext);
  if (ctx === null) {
    throw new Error("useSiteTheme must be used within SiteThemeProvider");
  }
  return ctx;
}

export function useTheme(): Theme {
  const { isDark } = useSiteTheme();
  return isDark ? darkTheme : lightTheme;
}
